/**
 * components/landing/home/HeroSection.jsx — Section 1 (Kalki Ai Cloud).
 *
 * Kinetic hero: masked line-by-line headline reveal on load, layered
 * gold-on-obsidian backdrop (radial wash + grid + starfield + rotating
 * AI ring), and the live platform dashboard preview with a subtle
 * scroll parallax. Pure CSS + SVG visuals — no heavy media.
 */
import { useEffect, useState, useMemo, useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { ArrowRight, Sparkles, ShieldCheck, Cpu, Zap, TrendingUp, TrendingDown } from 'lucide-react'
import { Container, Eyebrow, GradientButton, GhostButton } from '../design-system'
import { GlowCard, MockDashboardChrome, MiniSparkline, seededWalk } from './_shared'
import clsx from 'clsx'
import NextLink from '@/shims/NextLink'

const EASE = [0.22, 1, 0.36, 1]

const RevealLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.04em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: '112%' }}
      animate={{ y: '0%' }}
      transition={{ duration: 1.05, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
)

const Fade = ({ children, delay = 0, className = '' }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, delay, ease: EASE }}
  >
    {children}
  </motion.div>
)

const HeroSection = () => {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 2400)
    return () => clearInterval(id)
  }, [])

  const sparks = useMemo(() => ({
    btc: seededWalk(1, 42, 6, 63000),
    eth: seededWalk(2, 42, 4, 3400),
    equity: seededWalk(3, 60, 8, 120),
  }), [])

  const dashRef = useRef(null)
  const { scrollYProgress } = useScroll({ target: dashRef, offset: ['start end', 'end start'] })
  const yDash = useTransform(scrollYProgress, [0, 1], [36, -36])
  const yRing = useTransform(scrollYProgress, [0, 1], [90, -90])

  return (
    <section className="relative isolate overflow-hidden pt-40 md:pt-48 pb-24 md:pb-40">
      {/* ─── Animated backdrop ────────────────────────────────────── */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        {/* gold + emerald wash */}
        <div className="absolute inset-0 opacity-80" style={{ background: 'radial-gradient(55% 40% at 50% 0%, rgba(255,193,7,0.20), transparent 70%)' }} />
        <div className="absolute inset-0 opacity-40" style={{ background: 'radial-gradient(45% 30% at 85% 25%, rgba(16,185,129,0.10), transparent 70%)' }} />
        {/* grid + starfield */}
        <div className="absolute inset-0 opacity-[0.10] [background-image:linear-gradient(to_right,rgba(255,255,255,0.35)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.35)_1px,transparent_1px)] [background-size:64px_64px] [mask-image:radial-gradient(70%_50%_at_50%_40%,black,transparent)]" />
        <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(1px_1px_at_25%_20%,rgba(255,240,186,0.7),transparent),radial-gradient(1px_1px_at_60%_60%,rgba(255,193,7,0.55),transparent),radial-gradient(1px_1px_at_80%_35%,rgba(255,255,255,0.4),transparent),radial-gradient(1.5px_1.5px_at_15%_75%,rgba(245,228,162,0.5),transparent),radial-gradient(1px_1px_at_92%_80%,rgba(255,255,255,0.5),transparent)]" />
        {/* slow-rotating AI ring, parallax on scroll */}
        <motion.div style={{ y: yRing }} className="absolute inset-0">
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full opacity-30 animate-brand-spin-slow"
            style={{ background: 'conic-gradient(from 90deg, transparent 0deg, rgba(255,193,7,0.20) 90deg, transparent 180deg, rgba(16,185,129,0.10) 270deg, transparent 360deg)', mask: 'radial-gradient(farthest-side,transparent 62%, black 63%, black 64%, transparent 66%)' }}
          />
        </motion.div>
      </div>

      <Container>
        <div className="mx-auto max-w-5xl text-center flex flex-col items-center gap-6">
          <Fade delay={0.05}>
            <Eyebrow>Enterprise AI Trading Platform</Eyebrow>
          </Fade>

          <h1 data-testid="hero-headline" className="text-4xl sm:text-5xl md:text-6xl lg:text-[68px] font-semibold tracking-tight text-white leading-[1.08]">
            <RevealLine delay={0.15}>The infrastructure behind</RevealLine>
            <RevealLine delay={0.34}>
              <span className="bg-gradient-to-r from-brand-gold via-[#FFE17A] to-brand-gold bg-clip-text text-transparent">
                modern AI trading businesses
              </span>
              .
            </RevealLine>
          </h1>

          <Fade delay={0.6}>
            <p className="text-base md:text-xl text-zinc-400 max-w-3xl leading-relaxed">
              Kalki Ai Cloud combines AI intelligence, professional trading tools,
              education, automation, subscriptions, and White-Label deployment
              into one unified enterprise SaaS platform.
            </p>
          </Fade>

          <Fade delay={0.75} className="mt-3 flex flex-col sm:flex-row items-center gap-3">
            <GradientButton href="/contact" size="lg" data-testid="cta-button-schedule-demo">Schedule Live Demo</GradientButton>
            <GhostButton href="/platform" size="lg" data-testid="cta-button-explore-platform">Explore Platform</GhostButton>
            <NextLink href="#pricing" data-testid="cta-button-view-pricing" className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white transition-colors px-2 py-2">
              View pricing
              <ArrowRight className="h-3.5 w-3.5" />
            </NextLink>
          </Fade>

          <Fade delay={0.9} className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[12px] text-zinc-500 font-mono">
            <span className="inline-flex items-center gap-1.5"><Cpu className="h-3.5 w-3.5 text-brand-gold" />AI native</span>
            <span>·</span>
            <span>OpenAI + Anthropic ready</span>
            <span>·</span>
            <span>Multi-tenant</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-brand-emerald" />GDPR aligned</span>
          </Fade>
        </div>

        {/* ─── Live dashboard preview (parallax) ────────────────────── */}
        <motion.div ref={dashRef} style={{ y: yDash }} className="relative mt-20 md:mt-28">
          <GlowCard className="mx-auto max-w-5xl">
            <MockDashboardChrome innerClass="grid grid-cols-12 gap-0 min-h-[420px]">
              {/* Sidebar */}
              <aside className="col-span-2 border-r border-white/5 bg-black/30 hidden md:flex flex-col py-4 gap-1 text-[11px] text-zinc-300">
                {['Dashboard','AI Mentor','Trading','Backtesting','Scanner','Academy','Billing','Analytics'].map((label, i) => (
                  <button key={label} type="button" className={clsx(
                    'mx-2 px-3 py-2 rounded-lg text-left transition-colors flex items-center gap-2',
                    i === 0 ? 'bg-brand-gold/10 text-brand-gold' : 'text-zinc-400 hover:text-white hover:bg-white/[0.03]',
                  )}>
                    <span className={clsx('h-1.5 w-1.5 rounded-full', i === 0 ? 'bg-brand-gold' : 'bg-zinc-600')} />
                    {label}
                  </button>
                ))}
              </aside>
              {/* Main pane */}
              <div className="col-span-12 md:col-span-10 p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-zinc-500">Live tenant workspace</div>
                    <div className="text-white text-sm font-semibold mt-0.5">Portfolio Overview · <span className="text-zinc-500">as of now</span></div>
                  </div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-brand-emerald/25 bg-brand-emerald/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-brand-emerald">
                    <span className="h-1.5 w-1.5 rounded-full bg-brand-emerald animate-pulse" />
                    Live
                  </div>
                </div>

                {/* KPI row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Equity',    value: `$${(120000 + (tick % 8) * 340).toLocaleString()}`, delta: '+3.4%', tone: 'green', spark: sparks.equity },
                    { label: 'BTC/USDT',  value: `$${(63000 + (tick % 5) * 45).toLocaleString()}`,  delta: '+1.8%', tone: 'green', spark: sparks.btc },
                    { label: 'ETH/USDT',  value: `$${(3400 + (tick % 6) * 8).toLocaleString()}`,    delta: '-0.6%', tone: 'red',   spark: sparks.eth },
                    { label: 'Win-rate',  value: '61.4%',                                            delta: '+0.9pp',tone: 'green' },
                  ].map((k) => (
                    <div key={k.label} className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k.label}</div>
                        <div className={clsx('inline-flex items-center gap-0.5 text-[10px] font-mono', k.tone === 'green' ? 'text-brand-emerald' : 'text-red-400')}>
                          {k.tone === 'green' ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                          {k.delta}
                        </div>
                      </div>
                      <div className="text-lg font-semibold text-white mt-1 tabular-nums">{k.value}</div>
                      {k.spark && <MiniSparkline data={k.spark} stroke={k.tone === 'green' ? '#10B981' : '#ef4444'} height={22} width={140} className="mt-1 w-full" />}
                    </div>
                  ))}
                </div>

                {/* Chart + Mentor panel */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 min-h-[180px]">
                  <div className="md:col-span-2 rounded-xl border border-white/5 bg-white/[0.02] p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[11px] text-zinc-400">BTC/USDT · 4H</div>
                      <div className="flex items-center gap-1 text-[10px] text-zinc-500">
                        {['1H','4H','1D','1W'].map((tf) => (
                          <span key={tf} className={clsx('px-1.5 py-0.5 rounded', tf === '4H' ? 'bg-brand-gold/10 text-brand-gold' : '')}>{tf}</span>
                        ))}
                      </div>
                    </div>
                    <MiniSparkline data={sparks.btc} stroke="#FFC107" height={140} width={640} className="w-full" />
                  </div>
                  <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3 flex flex-col">
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-6 rounded-md bg-brand-gold/15 text-brand-gold flex items-center justify-center"><Sparkles className="h-3.5 w-3.5" /></div>
                      <div className="text-[11px] text-zinc-400">AI Mentor</div>
                    </div>
                    <div className="mt-3 space-y-2 text-[11px] leading-snug">
                      <div className="rounded-lg bg-white/[0.03] px-2.5 py-1.5 text-zinc-300">Why is BTC bullish today?</div>
                      <div className="rounded-lg bg-brand-gold/[0.08] border border-brand-gold/20 px-2.5 py-1.5 text-zinc-200">
                        BTC broke the 4H resistance at $63.4k with rising volume. Order block held. <span className="text-brand-gold">Confidence 74%</span>.
                      </div>
                      <div className="text-[10px] text-zinc-500 mt-1 font-mono">
                        Cites: HTF bias · 4H structure · volume profile
                      </div>
                    </div>
                    <div className="mt-auto flex items-center gap-1 pt-3 text-[10px] text-zinc-500">
                      <Zap className="h-3 w-3 text-brand-emerald" />
                      Streaming · grounded
                    </div>
                  </div>
                </div>
              </div>
            </MockDashboardChrome>
          </GlowCard>

          {/* Floating pill callouts around the dashboard */}
          <div className="hidden md:block absolute -left-6 top-16 rounded-full border border-white/10 bg-black/60 backdrop-blur px-3 py-1.5 text-[11px] text-zinc-300"><span className="text-brand-gold font-semibold">1-click</span> tenant provisioning</div>
          <div className="hidden md:block absolute -right-4 top-40 rounded-full border border-white/10 bg-black/60 backdrop-blur px-3 py-1.5 text-[11px] text-zinc-300">Sub-second execution</div>
          <div className="hidden md:block absolute left-8 -bottom-4 rounded-full border border-white/10 bg-black/60 backdrop-blur px-3 py-1.5 text-[11px] text-zinc-300">GDPR aligned</div>
        </motion.div>
      </Container>
    </section>
  )
}

export default HeroSection
